import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Phone, Menu, X, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/aurakart-logo.png';

const categories = [
  { name: 'Home', path: '/' },
  { name: 'All Products', path: '/products' },
  { name: 'Boys', path: '/category/boys' },
  { name: 'Girls', path: '/category/girls' },
  { name: 'Ladies', path: '/category/ladies' },
  { name: 'Men', path: '/category/men' },
];

export function Header() {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { totalItems } = useCart();
  const { wishlistCount } = useWishlist();
  const { user, setShowSignInModal } = useAuth();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-card shadow-nav">
      {/* Top bar */}
      <div className="bg-foreground text-card py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            {/* Support phone number removed as requested */}
          </div>
          <div className="flex items-center gap-4">
            {/* Links removed as requested */}
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Mobile menu button */}
          <button 
            className="lg:hidden p-2 hover:bg-secondary rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="AuraKart" className="h-10 sm:h-12" />
          </Link>

          {/* Search bar - desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl">
            <div className="relative w-full">
              <Input
                type="search"
                placeholder='Search "Sarees", "T Shirts", "Kurtas"...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 h-11 rounded-full border-2 border-secondary focus:border-primary transition-colors"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            </div>
          </form>

          {/* Icons */}
          <div className="flex items-center gap-2">
            <Link to="/search" className="md:hidden p-2 hover:bg-secondary rounded-lg transition-colors">
              <Search className="h-5 w-5" />
            </Link>
            
            <Link to="/wishlist" className="relative p-2 hover:bg-secondary rounded-lg transition-colors">
              <Heart className="h-5 w-5" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {wishlistCount}
                </span>
              )}
            </Link>

            <Link to="/cart" className="relative p-2 hover:bg-secondary rounded-lg transition-colors">
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              )}
            </Link>

            {/* User Authentication */}
            {user.isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative p-2 hover:bg-secondary rounded-lg transition-colors">
                    <div className="relative">
                      <User className="h-5 w-5" />
                      {/* Green dot to indicate signed in */}
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border border-background"></div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.firstName || user.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="cursor-pointer">
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Orders
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/logout" className="cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowSignInModal(true)}
                className="hidden sm:flex text-xs px-3 py-1.5 h-8"
              >
                Sign In
              </Button>
            )}
            
            {/* Mobile Sign In Icon */}
            {!user.isAuthenticated && (
              <button 
                onClick={() => setShowSignInModal(true)}
                className="sm:hidden p-2 hover:bg-secondary rounded-lg transition-colors relative"
              >
                <User className="h-5 w-5" />
                {/* Red dot to indicate not signed in */}
                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-background"></div>
              </button>
            )}
          </div>
        </div>

        {/* Mobile search */}
        <form onSubmit={handleSearch} className="mt-3 md:hidden">
          <div className="relative">
            <Input
              type="search"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 h-10 rounded-full border-2 border-secondary focus:border-primary"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </form>
      </div>

      {/* Category navigation - desktop */}
      <nav className="hidden lg:block border-t border-border">
        <div className="container mx-auto px-4">
          <ul className="flex items-center gap-1">
            {categories.map((cat) => (
              <li key={cat.path}>
                <Link
                  to={cat.path}
                  className="block px-4 py-3 font-medium hover:text-primary hover:bg-secondary/50 transition-colors rounded-lg"
                >
                  {cat.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-border bg-card"
          >
            <nav className="container mx-auto px-4 py-2">
              <ul className="space-y-1">
                {categories.map((cat) => (
                  <li key={cat.path}>
                    <Link
                      to={cat.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 font-medium hover:text-primary hover:bg-secondary/50 transition-colors rounded-lg"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
                
                {/* Mobile Authentication */}
                <div className="border-t border-border pt-2 mt-2">
                  {user.isAuthenticated ? (
                    <>
                      <li>
                        <div className="px-4 py-2 text-sm text-muted-foreground">
                          Signed in as {user.email}
                        </div>
                      </li>
                      <li>
                        <Link
                          to="/profile"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-3 font-medium hover:text-primary hover:bg-secondary/50 transition-colors rounded-lg"
                        >
                          Profile
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/orders"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-3 font-medium hover:text-primary hover:bg-secondary/50 transition-colors rounded-lg"
                        >
                          My Orders
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/logout"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-3 font-medium hover:text-primary hover:bg-secondary/50 transition-colors rounded-lg"
                        >
                          Log out
                        </Link>
                      </li>
                    </>
                  ) : (
                    <li>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setShowSignInModal(true);
                        }}
                        className="w-full justify-start"
                      >
                        Sign In
                      </Button>
                    </li>
                  )}
                </div>
              </ul>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
